import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Save, Trash2, Lock, LockOpen, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StickerReprintButton } from "@/components/StickerReprintButton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const COLORS = ["#0F8F4F", "#0EA5E9", "#A855F7", "#EF4444", "#FACC15", "#09090B"];

export default function Settings() {
  const { activeProperty, refreshProperties } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // PIN gate state — separate from the main form so hitting "Save
  // changes" never silently updates the PIN.
  const [pinInput, setPinInput] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [pinBusy, setPinBusy] = useState(false);

  useEffect(() => {
    if (!activeProperty) return;
    setForm({ ...activeProperty });
  }, [activeProperty]);

  if (!form) return <div className="text-sm text-[#71717A]">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/properties/${activeProperty.id}`, form);
      await refreshProperties();
      toast.success("Property updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const removeProperty = async () => {
    setDeleting(true);
    try {
      await api.delete(`/properties/${activeProperty.id}`);
      toast.success("Property deleted");
      await refreshProperties();
      nav("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const savePin = async () => {
    if (pinInput.length < 4 || pinInput.length > 10) {
      toast.error("PIN must be 4–10 digits");
      return;
    }
    if (pinInput !== pinConfirm) {
      toast.error("PIN entries don't match");
      return;
    }
    setPinBusy(true);
    try {
      await api.patch(`/properties/${activeProperty.id}`, {
        pin: pinInput,
        pin_required: true,
      });
      toast.success("PIN set · Private verification is now ON");
      setPinInput("");
      setPinConfirm("");
      await refreshProperties();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPinBusy(false);
    }
  };

  const resetPin = async () => {
    if (
      !window.confirm(
        "Reset the property PIN and disable private verification? Scanners will see the public verification page again."
      )
    )
      return;
    setPinBusy(true);
    try {
      await api.patch(`/properties/${activeProperty.id}`, {
        pin_reset: true,
      });
      toast.success("PIN cleared · Private verification is now OFF");
      setPinInput("");
      setPinConfirm("");
      await refreshProperties();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPinBusy(false);
    }
  };

  const togglePinRequired = async (next) => {
    if (next && !activeProperty.pin_set_at && !form.pin_hash) {
      toast.error("Set a PIN first before enabling private verification.");
      return;
    }
    setPinBusy(true);
    try {
      await api.patch(`/properties/${activeProperty.id}`, {
        pin_required: next,
      });
      toast.success(next ? "Private verification ON" : "Private verification OFF");
      await refreshProperties();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPinBusy(false);
    }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="mono-tiny text-[#71717A]">Settings</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">
          Property profile
        </h1>
      </div>

      <div className="bg-white border border-[#E4E4E7] rounded-lg p-6 md:p-8 space-y-4">
        <Row>
          <Field label="Corporation">
            <Input
              value={form.corporation_name || ""}
              onChange={(e) => set("corporation_name", e.target.value)}
              className="h-11"
              data-testid="settings-corp"
            />
          </Field>
          <Field label="Property name">
            <Input
              value={form.property_name || ""}
              onChange={(e) => set("property_name", e.target.value)}
              className="h-11"
              data-testid="settings-property"
            />
          </Field>
        </Row>
        <Field label="Address">
          <Input
            value={form.address || ""}
            onChange={(e) => set("address", e.target.value)}
            className="h-11"
            data-testid="settings-address"
          />
        </Field>
        <Row>
          <Field label="City">
            <Input value={form.city || ""} onChange={(e) => set("city", e.target.value)} className="h-11" />
          </Field>
          <Field label="State">
            <Input value={form.state || ""} onChange={(e) => set("state", e.target.value)} className="h-11" />
          </Field>
          <Field label="ZIP">
            <Input value={form.zip || ""} onChange={(e) => set("zip", e.target.value)} className="h-11" />
          </Field>
        </Row>
        <Row>
          <Field label="Manager name">
            <Input
              value={form.manager_name || ""}
              onChange={(e) => set("manager_name", e.target.value)}
              className="h-11"
            />
          </Field>
          <Field label="Manager phone">
            <Input
              value={form.manager_phone || ""}
              onChange={(e) => set("manager_phone", e.target.value)}
              className="h-11"
            />
          </Field>
        </Row>
        <Field label="Manager email">
          <Input
            type="email"
            value={form.manager_email || ""}
            onChange={(e) => set("manager_email", e.target.value)}
            className="h-11"
          />
        </Field>

        <Field label="QR color">
          <div className="flex flex-wrap gap-3">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => set("qr_color", c)}
                data-testid={`settings-color-${c.replace("#", "")}`}
                className={`w-11 h-11 rounded-md border-2 ${
                  form.qr_color === c ? "border-[#09090B] scale-110" : "border-[#E4E4E7]"
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </Field>

        <Field label="QR shape">
          <div className="flex flex-wrap gap-3">
            {[
              { key: "square", label: "Square" },
              { key: "rounded", label: "Rounded" },
              { key: "dots", label: "Dots" },
            ].map((s) => {
              const selected = (form.qr_style || "square") === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => set("qr_style", s.key)}
                  data-testid={`settings-shape-${s.key}`}
                  className={`px-4 h-11 rounded-md border-2 text-sm font-medium flex items-center gap-2 transition ${
                    selected
                      ? "border-[#0F8F4F] bg-[#0F8F4F]/5 text-[#0A6B3B]"
                      : "border-[#E4E4E7] text-[#3F3F46] hover:border-[#0F8F4F]/40"
                  }`}
                >
                  <QrShapeIcon shape={s.key} color={form.qr_color || "#0F8F4F"} />
                  {s.label}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-[#71717A] mt-2">
            Applies to every QR code and printed sticker for this property. Rounded
            and dots use styled module drawers — scanners still read them fine.
          </p>
        </Field>
      </div>

      <Button
        onClick={save}
        disabled={saving}
        className="h-12 bg-[#0F8F4F] hover:bg-[#0A6B3B]"
        data-testid="settings-save"
      >
        <Save className="w-4 h-4 mr-2" /> Save changes
      </Button>

      {/* === Private QR verification (opt-in PIN gate) ===
          Enabling this hides all vehicle/tenant details on the public
          scan page until the scanner enters the property PIN. A
          correct PIN unlocks the device for 10 minutes via an
          httponly cookie so parking enforcement, security, and towing
          don't have to retype the PIN on every car at the same site.
          5 wrong tries → 10-min cooldown. */}
      <div
        className="bg-white border border-[#E4E4E7] rounded-lg p-5 md:p-6 mt-6"
        data-testid="settings-pin-card"
      >
        <div className="flex items-center gap-3">
          {activeProperty.pin_required ? (
            <Lock className="w-5 h-5 text-[#0F8F4F]" strokeWidth={1.75} />
          ) : (
            <LockOpen className="w-5 h-5 text-[#71717A]" strokeWidth={1.5} />
          )}
          <div className="flex-1">
            <div className="mono-tiny text-[#71717A]">Verification mode</div>
            <div className="font-display font-bold text-base">
              {activeProperty.pin_required
                ? "Private · PIN required"
                : "Public · No PIN"}
            </div>
          </div>
          {activeProperty.pin_set_at && (
            <button
              type="button"
              onClick={() => togglePinRequired(!activeProperty.pin_required)}
              disabled={pinBusy}
              data-testid="settings-pin-toggle"
              className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                activeProperty.pin_required ? "bg-[#0F8F4F]" : "bg-[#E4E4E7]"
              }`}
              aria-pressed={activeProperty.pin_required}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${
                  activeProperty.pin_required ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          )}
        </div>
        <p className="text-sm text-[#71717A] mt-3 max-w-xl">
          Private verification hides vehicle &amp; tenant info from the public
          scan page. Scanners (parking enforcement, security, towing, property
          staff) enter the property PIN once per device; a 10-minute cookie
          keeps them unlocked while they walk the lot. 5 wrong PINs = 10-min
          cooldown.
        </p>

        <div className="grid md:grid-cols-2 gap-3 mt-5">
          <Field label={activeProperty.pin_set_at ? "New PIN (4–10 digits)" : "Set PIN (4–10 digits)"}>
            <Input
              type="password"
              inputMode="numeric"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="••••"
              className="h-11 font-mono tracking-widest"
              data-testid="settings-pin-input"
            />
          </Field>
          <Field label="Confirm PIN">
            <Input
              type="password"
              inputMode="numeric"
              value={pinConfirm}
              onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="••••"
              className="h-11 font-mono tracking-widest"
              data-testid="settings-pin-confirm"
            />
          </Field>
        </div>

        <div className="flex items-center gap-3 flex-wrap mt-4">
          <Button
            onClick={savePin}
            disabled={pinBusy || !pinInput}
            className="bg-[#0F8F4F] hover:bg-[#0A6B3B]"
            data-testid="settings-pin-save"
          >
            {pinBusy ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Lock className="w-4 h-4 mr-2" strokeWidth={1.75} />
            )}
            {activeProperty.pin_set_at ? "Update PIN" : "Set PIN & enable"}
          </Button>
          {activeProperty.pin_set_at && (
            <Button
              onClick={resetPin}
              variant="outline"
              disabled={pinBusy}
              className="border-[#EF4444]/40 text-[#B91C1C] hover:bg-[#FEE2E2]"
              data-testid="settings-pin-reset"
            >
              <LockOpen className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Clear PIN
            </Button>
          )}
          {activeProperty.pin_set_at && (
            <span className="mono-tiny text-[#71717A]">
              PIN last set {new Date(activeProperty.pin_set_at).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      {/* Printer reprint quick action — same behaviour as the Stickers
          page but lives here so managers reviewing branding/QR settings
          can trigger a reprint the moment they change QR color/shape. */}
      <div
        className="bg-white border border-[#E4E4E7] rounded-lg p-5 flex items-center justify-between flex-wrap gap-3 mt-6"
        data-testid="settings-reprint-card"
      >
        <div>
          <div className="mono-tiny text-[#71717A]">Printer</div>
          <div className="font-display font-bold text-base mt-1">
            Request a printer reprint
          </div>
          <div className="text-sm text-[#71717A] mt-1 max-w-lg">
            Changed the QR color/shape or need a fresh batch? Pay $50 and our
            print team will kick off a new run.
          </div>
        </div>
        <StickerReprintButton
          propertyId={activeProperty.id}
          testId="settings-reprint-btn"
        />
      </div>

      <div className="border border-[#EF4444]/30 bg-[#FEF2F2] rounded-lg p-6 mt-12" data-testid="settings-danger-zone">
        <div className="mono-tiny text-[#B91C1C]">Danger zone</div>
        <h2 className="font-display text-xl font-bold mt-1 text-[#09090B]">Delete this property</h2>
        <p className="text-sm text-[#7F1D1D] mt-2">
          Deleting this property removes <b>all tenants, vehicles, QR codes, sticker orders, and
          billing records</b> for this property from your dashboard. This action cannot be undone.
        </p>
        <Button
          onClick={() => setConfirmDelete(true)}
          variant="outline"
          className="mt-4 border-[#EF4444] text-[#B91C1C] hover:bg-[#EF4444] hover:text-white"
          data-testid="settings-delete-property"
        >
          <Trash2 className="w-4 h-4 mr-2" /> Delete property
        </Button>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="property-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this property?</AlertDialogTitle>
            <AlertDialogDescription>
              You're about to permanently delete{" "}
              <b className="text-[#09090B]">{activeProperty.property_name}</b>. This will remove
              all tenants, vehicles, QR codes, sticker orders, and billing history tied to this
              property. <b>This cannot be undone.</b>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="property-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                removeProperty();
              }}
              disabled={deleting}
              className="bg-[#EF4444] hover:bg-[#DC2626]"
              data-testid="property-delete-confirm"
            >
              {deleting ? "Deleting…" : "Delete property permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Row({ children }) {
  return <div className={`grid grid-cols-1 md:grid-cols-${React.Children.count(children)} gap-3`}>{children}</div>;
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="mono-tiny text-[#71717A]">{label}</Label>
      {children}
    </div>
  );
}

/**
 * Tiny inline preview of the QR module shape — a 3x3 grid rendered
 * as SVG at 16px so the picker option looks like the actual QR
 * texture. Not a real QR — purely decorative.
 */
function QrShapeIcon({ shape, color }) {
  const cells = [];
  for (let y = 0; y < 3; y++) {
    for (let x = 0; x < 3; x++) {
      const on = (x + y) % 2 === 0;
      if (!on) continue;
      if (shape === "dots") {
        cells.push(<circle key={`${x}${y}`} cx={x * 5 + 2.5} cy={y * 5 + 2.5} r={2} fill={color} />);
      } else if (shape === "rounded") {
        cells.push(
          <rect key={`${x}${y}`} x={x * 5 + 0.5} y={y * 5 + 0.5} width={4} height={4} rx={1.5} fill={color} />
        );
      } else {
        cells.push(<rect key={`${x}${y}`} x={x * 5 + 0.5} y={y * 5 + 0.5} width={4} height={4} fill={color} />);
      }
    }
  }
  return (
    <svg width={16} height={16} viewBox="0 0 15 15" aria-hidden="true">
      {cells}
    </svg>
  );
}
